import type {
  ReactNode,
  CSSProperties,
  KeyboardEvent,
  ChangeEvent,
  MouseEvent,
  FocusEvent,
  Ref
} from 'react';

export interface BaseTextAreaProps {
  /** Placeholder text for the textarea */
  placeholder?: string;

  /** Current value of the textarea */
  value?: string;

  /** Change handler for controlled textarea */
  onChange?: TextAreaElementChangeHandler;

  /** Key down event handler */
  onKeyDown?: TextAreaElementKeyboardHandler;

  /** Click event handler */
  onClick?: TextAreaElementMouseHandler;

  /** Mouse move event handler */
  onMouseMove?: TextAreaElementMouseHandler;

  /** Mouse leave event handler */
  onMouseLeave?: TextAreaElementMouseHandler;

  /** Blur event handler */
  onBlur?: TextAreaElementFocusHandler;

  /** Submit handler triggered by Ctrl+Enter or button click */
  onSubmit?: (value: string) => void;

  /** External reference to the textarea element */
  ref?: Ref<HTMLTextAreaElement>;

  /** Additional CSS class names */
  className?: string;

  /** Inline styles */
  style?: CSSProperties;

  /** Enable auto-grow functionality */
  autoGrow?: boolean;

  /** Minimum number of rows */
  minRows?: number;

  /** Maximum number of rows */
  maxRows?: number;

  /** Show submit button */
  showSubmitButton?: boolean;

  /** Show clear button */
  showClearButton?: boolean;

  /** Submit button text */
  submitButtonText?: string;

  /** Clear button text */
  clearButtonText?: string;

  /** Child elements for flexible UI extension */
  children?: ReactNode;

  /** TextArea ID */
  id?: string;

  /** Disable browser autocomplete */
  autoComplete?: string;

  /** Rows attribute (when autoGrow is false) */
  rows?: number;

  /** Cols attribute */
  cols?: number;

  /** Wrap attribute */
  wrap?: string;

  /** Max length (character count) for the textarea */
  maxLength?: number;
}

// Event handler types for textarea
export type TextAreaElementChangeHandler = (
  event: ChangeEvent<HTMLTextAreaElement>
) => void;
export type TextAreaElementKeyboardHandler = (
  event: KeyboardEvent<HTMLTextAreaElement>
) => void;
export type TextAreaElementMouseHandler = (
  event: MouseEvent<HTMLTextAreaElement>
) => void;
export type TextAreaElementFocusHandler = (
  event: FocusEvent<HTMLTextAreaElement>
) => void;

// Ref types
export type TextAreaElementRef =
  | React.RefObject<HTMLTextAreaElement>
  | React.MutableRefObject<HTMLTextAreaElement | null>;

// Utility function for synthetic events
export const createSyntheticChangeEvent = (
  textareaElement: HTMLTextAreaElement,
  newValue: string
): ChangeEvent<HTMLTextAreaElement> => {
  textareaElement.value = newValue;

  const event = new Event('change', { bubbles: true, cancelable: true });

  return Object.create(event, {
    target: { value: textareaElement, enumerable: true },
    currentTarget: { value: textareaElement, enumerable: true },
    type: { value: 'change', enumerable: true },
    nativeEvent: { value: event, enumerable: true },
    preventDefault: { value: () => event.preventDefault(), enumerable: true },
    stopPropagation: { value: () => event.stopPropagation(), enumerable: true },
    isDefaultPrevented: {
      value: () => event.defaultPrevented,
      enumerable: true
    },
    isPropagationStopped: { value: () => false, enumerable: true },
    persist: { value: () => {}, enumerable: true },
    timeStamp: { value: Date.now(), enumerable: true }
  }) as ChangeEvent<HTMLTextAreaElement>;
};
